import React from 'react';
import { Plus, Receipt, Wallet } from 'lucide-react';
import { Button } from '../../components/Button';
import type { Expense, UserProfile } from '../../types';
import { format } from 'date-fns';
import { formatCurrency } from '../../utils/currencyFormatting';
import type { User } from 'firebase/auth';

interface GroupExpensesListProps {
    expenses: Expense[];
    memberProfiles: UserProfile[];
    currentUser: User | null | undefined;
    onEditExpense: (expense: Expense) => void;
    onResetForm: () => void;
    onOpenAddModal: () => void;
}

export const GroupExpensesList: React.FC<GroupExpensesListProps> = ({
    expenses,
    memberProfiles,
    currentUser,
    onEditExpense,
    onResetForm,
    onOpenAddModal
}) => {
    return (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-text-heading">History</h3>
                <Button size="sm" onClick={() => { onResetForm(); onOpenAddModal(); }}>
                    <Plus className="mr-1 h-4 w-4" />
                    Add Expense
                </Button>
            </div>

            {expenses.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="mb-4 rounded-full bg-bg-card-alt p-4">
                        <Receipt className="h-8 w-8 text-text-faint" />
                    </div>
                    <p className="text-sm text-text-muted">No expenses yet.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {expenses.map((expense) => (
                        <div
                            key={expense.id}
                            onClick={() => { onEditExpense(expense); }}
                            className="flex items-center justify-between rounded-xl border border-border-default bg-bg-card p-4 shadow-sm active:scale-[0.98] transition-all cursor-pointer hover:border-primary-200"
                        >
                            <div className="flex items-center gap-3">
                                <div className="rounded-full bg-bg-card-alt p-2">
                                    <Wallet className="h-5 w-5 text-text-muted" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-text-heading">{expense.description}</h4>
                                    <p className="text-xs text-text-muted">
                                        {format(new Date(expense.date), 'MMM d, yyyy')} • Paid by {expense.payerId === currentUser?.uid ? 'You' : (memberProfiles.find(p => p.uid === expense.payerId)?.displayName ?? 'Member')}
                                    </p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="font-bold text-text-heading">{formatCurrency(expense.amount, expense.currency)}</p>
                                {expense.originalCurrency && expense.originalCurrency !== expense.currency && (
                                    <p className="text-[10px] text-text-faint">
                                        Orig: {formatCurrency(expense.originalAmount ?? 0, expense.originalCurrency)}
                                    </p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
